import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Statistic, Spin, Tabs, Select, Space } from 'antd';
import { UserOutlined, DatabaseOutlined, FileTextOutlined, PhoneOutlined, ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { Line, Column } from '@ant-design/charts';
import { adminService } from '../../../services';
import { formatMexicoTime, calculateMexicoWeekStats, getCurrentMexicoTime } from '../../../utils/timeUtils';
import type { AdminDashboard, AdminManualEntryStats, ManualEntryEmployeesStats, Employee } from '../../../types';

const AdminDashboardPage: React.FC = () => {
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [manualStats, setManualStats] = useState<AdminManualEntryStats | null>(null);
  const [employeesStats, setEmployeesStats] = useState<ManualEntryEmployeesStats | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | undefined>();
  const [activeTab, setActiveTab] = useState<'overview' | 'weekly'>('overview');
  const [chartVisible, setChartVisible] = useState(true);
  const [allUploadTasks, setAllUploadTasks] = useState<any[]>([]);

  // 强制图表重新渲染
  const forceChartRerender = () => {
    setChartVisible(false);
    setTimeout(() => {
      setChartVisible(true);
    }, 100);
  };

  useEffect(() => {
    const fetchData = async () => {
        try {
          setLoading(true);
          const [dashboardResponse, employeesResponse, employeesStatsResponse, uploadsResponse] = await Promise.all([
            adminService.getDashboard(),
            adminService.getEmployees(),
            adminService.getManualEntryEmployeesStats(),
            adminService.getAllUploadTasksUnlimited(), // 使用无限制API
          ]);
          
          setData(dashboardResponse);
          setEmployees(employeesResponse.employees || []);
          setEmployeesStats(employeesStatsResponse);
          setAllUploadTasks(uploadsResponse.upload_tasks || []);
      } catch (error) {
        console.error('获取仪表板数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 获取选中员工的手动入库统计
  useEffect(() => {
    if (selectedEmployee) {
      const fetchManualStats = async () => {
        try {
          const response = await adminService.getManualEntryStats(selectedEmployee);
          setManualStats(response);
        } catch (error) {
          console.error('获取手动入库统计失败:', error);
        }
      };
      fetchManualStats();
    } else {
      setManualStats(null);
    }
    }, [selectedEmployee]);

  // 计算每个员工的批量导入统计（按周）
  const getEmployeeUploadStats = () => {
    const now = getCurrentMexicoTime();
    const currentWeekStart = new Date(now);
    currentWeekStart.setDate(now.getDate() - now.getDay() + 1);
    currentWeekStart.setHours(0, 0, 0, 0);
    
    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(currentWeekStart.getDate() - 7);
    
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    lastWeekEnd.setHours(23, 59, 59, 999);
    
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);

    const employeeStats: Record<number, { currentWeek: number; lastWeek: number; total: number }> = {};

    allUploadTasks.forEach(task => {
      if (task.status !== 'completed') return;
      
      // 后端返回的created_at是墨西哥时间，但不包含时区信息
      // 我们需要将其作为墨西哥时间（UTC-6）来解析
      const taskDateStr = task.created_at;
      
      // 直接解析为墨西哥时间，不进行时区转换
      // 因为后端存储的就是墨西哥时间
      const taskDate = new Date(taskDateStr);
      
      const employeeId = task.uploaded_by;
      
      if (!employeeStats[employeeId]) {
        employeeStats[employeeId] = { currentWeek: 0, lastWeek: 0, total: 0 };
      }
      
      const newPhonesAdded = task.new_phones_added || 0;
      employeeStats[employeeId].total += newPhonesAdded;
      
      if (taskDate >= currentWeekStart && taskDate <= currentWeekEnd) {
        employeeStats[employeeId].currentWeek += newPhonesAdded;
      } else if (taskDate >= lastWeekStart && taskDate <= lastWeekEnd) {
        employeeStats[employeeId].lastWeek += newPhonesAdded;
      }
    });

    return employeeStats;
  };

  // 计算选中员工的批量导入每日统计
  const getSelectedEmployeeDailyUploadStats = () => {
    if (!selectedEmployee) return { currentWeekDaily: Array(7).fill(0), lastWeekDaily: Array(7).fill(0) };
    
    // 过滤出选中员工的任务
    const employeeTasks = allUploadTasks.filter(task => task.uploaded_by === selectedEmployee);
    return calculateMexicoWeekStats(employeeTasks);
  };

  // 生成周统计图表数据（合并手动入库和批量导入）
  const getWeeklyChartData = () => {
    if (!manualStats) return [];
    
    const { currentWeekDaily, lastWeekDaily } = getSelectedEmployeeDailyUploadStats();
    const data = [];
    const currentWeekData = manualStats.current_week.daily_data;
    const lastWeekData = manualStats.last_week.daily_data;

    for (let i = 0; i < 7; i++) {
      const currentDay = currentWeekData[i];
      const lastDay = lastWeekData[i];
      
      data.push({
        day: currentDay.day_name,
        week: '本周',
        count: (currentDay.count || 0) + currentWeekDaily[i],
      });
      
      data.push({
        day: lastDay.day_name,
        week: '上周',
        count: (lastDay.count || 0) + lastWeekDaily[i],
      });
    }
    
    return data;
  };

  // 生成月度统计图表数据（合并手动入库和批量导入）
  const getMonthlyChartData = () => {
    if (!employeesStats) return [];
    
    const uploadStats = getEmployeeUploadStats();
    
    // 使用员工统计数据生成图表数据
    const data: Array<{
      employee: string;
      current_week: number;
      last_week: number;
      total_entries: number;
    }> = [];
    
    employeesStats.employees_stats.forEach(employee => {
      const employeeId = employee.employee.id;
      const uploadStat = uploadStats[employeeId] || { currentWeek: 0, lastWeek: 0, total: 0 };
      
      data.push({
        employee: employee.employee.username,
        current_week: employee.current_week + uploadStat.currentWeek,
        last_week: employee.last_week + uploadStat.lastWeek,
        total_entries: employee.total_entries + uploadStat.total,
      });
    });
    
    return data;
  };

  const weeklyChartConfig = {
    data: getWeeklyChartData(),
    xField: 'day',
    yField: 'count',
    seriesField: 'week',
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    legend: {
      position: 'top' as const,
    },
    // 使用对象形式配置颜色映射
    colorField: 'week',
    colorMapping: {
      '上周': '#1890ff',  // 蓝色
      '本周': '#52c41a',  // 绿色
    },
  };

  const monthlyChartConfig = {
    data: getMonthlyChartData(),
    xField: 'employee',
    yField: 'total_entries',
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: '#1890ff',
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <h1>管理员仪表板</h1>
      
      {/* 基础统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总数据集数量"
              value={data?.total_datasets || 0}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总员工数量"
              value={data?.total_employees || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总记录数量"
              value={data?.total_records || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

        {/* 入库统计卡片 */}
        {employeesStats && (() => {
          // 直接计算本周批量导入总数
          const uploadStats = getEmployeeUploadStats();
          const totalBatchCurrentWeek = Object.values(uploadStats).reduce((sum, stat) => sum + stat.currentWeek, 0);
          const totalBatchLastWeek = Object.values(uploadStats).reduce((sum, stat) => sum + stat.lastWeek, 0);
          
          // 直接计算本周手动入库总数
          const totalManualCurrentWeek = employeesStats.employees_stats.reduce((sum, emp) => sum + emp.current_week, 0);
          const totalManualLastWeek = employeesStats.employees_stats.reduce((sum, emp) => sum + emp.last_week, 0);
          
          // 总入库数 = 批量导入 + 手动入库
          const totalCurrentWeek = totalBatchCurrentWeek + totalManualCurrentWeek;
          const totalLastWeek = totalBatchLastWeek + totalManualLastWeek;
          
          return (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="本周总入库（手动+批量）"
                    value={totalCurrentWeek}
                    prefix={<PhoneOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="上周总入库（手动+批量）"
                    value={totalLastWeek}
                    prefix={<PhoneOutlined />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="本周活跃员工"
                    value={employeesStats.employees_stats.filter(emp => {
                      const uploadStat = uploadStats[emp.employee.id] || { currentWeek: 0, lastWeek: 0, total: 0 };
                      return (emp.current_week + uploadStat.currentWeek) > 0;
                    }).length}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
          );
        })()}

      {/* 统计图表标签页 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as any);
            forceChartRerender();
          }}
          items={[
            {
              key: 'overview',
              label: '全员概览',
              children: employeesStats ? (
                <div>
                  <Card title="员工总入库统计" style={{ marginBottom: 24 }}>
                    {chartVisible && <Column {...monthlyChartConfig} />}
                  </Card>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 50 }}>
                  <p>暂无统计数据</p>
                </div>
              ),
            },
            {
              key: 'weekly',
              label: '周度统计',
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Space>
                      <span>选择员工:</span>
                      <Select
                        placeholder="选择员工查看周度统计"
                        value={selectedEmployee}
                        onChange={setSelectedEmployee}
                        allowClear
                        style={{ width: 200 }}
                      >
                        {employees.map(employee => (
                          <Select.Option key={employee.id} value={employee.id}>
                            {employee.username}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </div>
                  
                  {manualStats ? (
                    <div>
                      
                      
                      <Card title="周度入库趋势对比（手动+批量）">
                        {chartVisible && <Line {...weeklyChartConfig} />}
                      </Card>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                      <p>请选择员工查看周度统计数据</p>
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AdminDashboardPage;
